(function (root) {
  // Set to false to hide swatches with no in-stock combination at the selected location.
  const SHOW_FULLY_UNAVAILABLE_SWATCHES = true;
  const controllers = new Map();
  const pendingMounts = new Map();

  function mount(config) {
    const sectionKey = String(config.sectionId);
    pendingMounts.set(sectionKey, config);
    mountWhenReady(config, 60);
  }

  function mountWhenReady(config, attempts) {
    const sectionKey = String(config.sectionId);

    if (pendingMounts.get(sectionKey) !== config) {
      return;
    }

    const selectorRoot = document.querySelector(config.rootSelector);

    if (!selectorRoot) {
      pendingMounts.delete(sectionKey);
      return;
    }

    if (!productFormVueForSection(config.sectionId)) {
      if (attempts > 0) {
        requestAnimationFrame(() => mountWhenReady(config, attempts - 1));
      } else {
        pendingMounts.delete(sectionKey);
      }

      return;
    }

    pendingMounts.delete(sectionKey);
    const existingController = controllers.get(sectionKey);

    if (
      existingController &&
      existingController.selectorRoot === selectorRoot &&
      existingController.config === config
    ) {
      return;
    }

    if (existingController) {
      existingController.destroy();
    }

    selectorRoot.dataset.refurbishedMounted = 'true';

    const { options, swatches } = readOptions(selectorRoot);
    const availability = root.RefurbishedAvailability.create({
      fixed: { location: config.location },
      options,
      showFullyUnavailableSwatches: SHOW_FULLY_UNAVAILABLE_SWATCHES,
      variants: config.variants,
    });
    const variantsById = new Map(
      config.variants.map((variant) => [String(variant.id), variant]),
    );
    const initialVariant = variantsById.get(String(config.initialVariantId));
    let currentSelection = initialVariant
      ? selectionForAttributes(options, initialVariant.attributes)
      : firstDisplayedSelection(options);
    let currentMediaVariantId = initialVariant ? initialVariant.id : null;
    let applyVersion = 0;

    function apply(result) {
      applyVersion += 1;
      const version = applyVersion;
      currentSelection = { ...result.selection };

      for (const [optionId, values] of swatches) {
        for (const [valueId, swatch] of values) {
          const state = result.states[optionId][valueId];
          const selected = result.selection[optionId] === valueId;

          swatch.container.hidden = state === 'hidden';
          swatch.container.dataset.refurbishedState = state;
          swatch.container.classList.toggle(
            'swatch-container--sold-out',
            state === 'struck',
          );
          swatch.input.checked = selected;
        }
      }

      syncGroupVisibility(selectorRoot);

      const storeSelection = storeSelectionFor(
        options,
        result.selection,
        config.location,
      );
      Alpine.store('variantOptions', storeSelection);
      Alpine.store('variantId', result.variantId);
      Alpine.store('currentVariantIsInStock', result.canAddToCart);

      const form = selectorRoot.closest('form');
      const variantSelector = form && form.querySelector('select[name="id"]');

      if (variantSelector) {
        variantSelector.value = result.variantId == null
          ? ''
          : String(result.variantId);
      }

      const selectedVariant = result.variantId == null
        ? null
        : variantsById.get(String(result.variantId));

      if (result.canAddToCart && result.variantId != null) {
        currentMediaVariantId = result.variantId;
      }

      syncVueProductForm(
        config.sectionId,
        selectedVariant,
        () => version === applyVersion,
      );

      document.dispatchEvent(
        new CustomEvent('variant-selected', {
          detail: { variantId: currentMediaVariantId },
        }),
      );
    }

    selectorRoot.addEventListener('change', (event) => {
      const input = event.target.closest(
        '[data-refurbished-option] input[type="radio"]',
      );

      if (!input || !selectorRoot.contains(input)) {
        return;
      }

      const container = input.closest('[data-refurbished-option]');
      const result = availability.select(
        currentSelection,
        container.dataset.refurbishedOption,
        container.dataset.refurbishedValue,
      );

      apply(result);
    });

    function requestVariant(event) {
      if (
        !event.detail ||
        String(event.detail.sectionId) !== String(config.sectionId) ||
        event.detail.variantId == null
      ) {
        return;
      }

      const targetVariant = variantsById.get(String(event.detail.variantId));

      if (!targetVariant) {
        return;
      }

      const targetSelection = selectionForAttributes(
        options,
        targetVariant.attributes,
      );
      const exactResult = availability.initialise(targetSelection);

      if (String(exactResult.variantId) === String(targetVariant.id)) {
        apply(exactResult);
        return;
      }

      let result;

      for (const option of options) {
        result = availability.select(
          currentSelection,
          option.id,
          targetSelection[option.id],
        );
        currentSelection = result.selection;
      }

      apply(result);
    }

    function reloadSection(event) {
      if (!event.detail || String(event.detail.sectionId) !== sectionKey) {
        return;
      }

      destroy();
      mount(config);
    }

    function unloadSection(event) {
      if (event.detail && String(event.detail.sectionId) === sectionKey) {
        destroy();
      }
    }

    function destroy() {
      applyVersion += 1;

      if (pendingMounts.get(sectionKey) === config) {
        pendingMounts.delete(sectionKey);
      }

      document.removeEventListener(
        'refurbished-variant-requested',
        requestVariant,
      );
      document.removeEventListener('shopify:section:load', reloadSection);
      document.removeEventListener('shopify:section:unload', unloadSection);

      if (controllers.get(sectionKey) === controller) {
        controllers.delete(sectionKey);
      }
    }

    const controller = { config, destroy, selectorRoot };
    controllers.set(sectionKey, controller);
    document.addEventListener('refurbished-variant-requested', requestVariant);
    document.addEventListener('shopify:section:load', reloadSection);
    document.addEventListener('shopify:section:unload', unloadSection);

    apply(availability.initialise(currentSelection));
  }

  function readOptions(selectorRoot) {
    const optionsById = new Map();
    const swatches = new Map();
    const containers = selectorRoot.querySelectorAll('[data-refurbished-option]');

    for (const container of containers) {
      const optionId = container.dataset.refurbishedOption;
      const valueId = container.dataset.refurbishedValue;
      const input = container.querySelector('input[type="radio"]');

      if (!input) {
        continue;
      }

      if (!optionsById.has(optionId)) {
        optionsById.set(optionId, { id: optionId, values: [] });
        swatches.set(optionId, new Map());
      }

      const value = {
        id: valueId,
        match: { [optionId]: container.dataset.refurbishedMatch },
        store: readStoreValues(container.dataset),
      };

      optionsById.get(optionId).values.push(value);
      swatches.get(optionId).set(valueId, { container, input });
    }

    return { options: Array.from(optionsById.values()), swatches };
  }

  function readStoreValues(dataset) {
    return Object.fromEntries(
      Object.entries(dataset)
        .filter(([key]) => key.startsWith('refurbishedStore'))
        .map(([key, value]) => [
          lowerFirst(key.slice('refurbishedStore'.length)),
          value,
        ]),
    );
  }

  function lowerFirst(value) {
    return value.charAt(0).toLowerCase() + value.slice(1);
  }

  function selectionForAttributes(options, attributes) {
    return Object.fromEntries(
      options.map((option) => {
        const value = option.values.find((candidate) =>
          Object.entries(candidate.match).every(
            ([key, expected]) => attributes[key] === expected,
          ),
        );

        return [option.id, value ? value.id : option.values[0].id];
      }),
    );
  }

  function firstDisplayedSelection(options) {
    return Object.fromEntries(
      options.map((option) => [option.id, option.values[0].id]),
    );
  }

  function storeSelectionFor(options, selection, location) {
    const storeSelection = { location };

    for (const option of options) {
      const value = option.values.find(
        (candidate) => candidate.id === selection[option.id],
      );

      if (value) {
        Object.assign(storeSelection, value.store);
      }
    }

    return storeSelection;
  }

  function syncVueProductForm(sectionId, variant, isCurrent, attempts = 20) {
    if (!variant || !isCurrent()) {
      return;
    }

    const vue = productFormVueForSection(sectionId);

    if (!vue) {
      if (attempts > 0 && isCurrent()) {
        requestAnimationFrame(() =>
          syncVueProductForm(sectionId, variant, isCurrent, attempts - 1),
        );
      }

      return;
    }

    vue.option1 = variant.data.option1;
    vue.option2 = variant.data.option2;
    vue.option3 = variant.data.option3;
  }

  function productFormVueForSection(sectionId) {
    const section = document.querySelector(
      `[data-wetheme-section-id="${sectionId}"]`,
    );
    const host = section && section.querySelector('.product-form-vue');

    return findProductFormVue(host && host.__vue__);
  }

  function findProductFormVue(vue) {
    if (!vue) {
      return null;
    }

    if (vue.product && Array.isArray(vue.product.variants)) {
      return vue;
    }

    for (const child of vue.$children || []) {
      const productForm = findProductFormVue(child);

      if (productForm) {
        return productForm;
      }
    }

    return null;
  }

  function syncGroupVisibility(selectorRoot) {
    const groups = selectorRoot.querySelectorAll(
      '._fabric-group, ._material-group',
    );

    for (const group of groups) {
      const swatchContainers = group.querySelectorAll(
        '[data-refurbished-option]',
      );
      const hidden = Array.from(swatchContainers).every(
        (container) => container.hidden,
      );
      group.hidden = hidden;
      group.style.display = hidden ? 'none' : 'grid';
    }

    const rows = selectorRoot.querySelectorAll(
      '._color_swatches, ._leg_color_swatches',
    );

    for (const row of rows) {
      const children = Array.from(row.children);
      const visibleGroups = children.filter(
        (child) =>
          (child.classList.contains('_fabric-group') ||
            child.classList.contains('_material-group')) &&
          !child.hidden,
      );

      for (const separator of row.querySelectorAll(':scope > ._separator')) {
        separator.hidden = true;
      }

      for (const group of visibleGroups.slice(0, -1)) {
        const followingChildren = children.slice(children.indexOf(group) + 1);
        const separator = followingChildren.find((child) =>
          child.classList.contains('_separator'),
        );

        if (separator) {
          separator.hidden = false;
        }
      }
    }
  }

  root.RefurbishedVariantSelector = { mount };
})(typeof window !== 'undefined' ? window : globalThis);
